import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import {
  Box,
  Divider,
  Drawer,
  lighten,
  List,
  MenuItem as MuiMenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { brand } from "../../theme/themePrimitives";
import { MessageType } from "../../components/PageLayout/PageLayout";
import { useCustomisation } from "../../context/CustomisationContext";
import { usePortal } from "../../context/PortalsContext";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { getShadeColour } from "../../utils/helpers";
import { isModifiedClick } from "../../utils/navigationClick";
import {
  hasAdminAccess,
  hasAnalyticsAccess,
  hasPortalConfigurationAccess,
  hasFileHubAccess,
  hasEmailManagementAccess,
  hasUserManagementAccess,
  hasUserImportJobAccess,
  hasPortalConfigAccess,
  hasAssignmentManagementAccess,
} from "../../utils/roleUtils";
import { terminateWithoutRedirect } from "../Scorm/scormApi";
import { useTranslate } from "../../hooks/useTranslate";
import { LanguageChooser } from "./LanguageChooser";
import { MenuItem } from "./MenuItem";
import { defaultNav, MainMenuItem, portalDesignNav } from "./sidebarData";
import { SidebarHeader } from "./SidebarHeader";
import { UserMenu } from "./UserMenu";

export const DRAWER_WIDTH = 280;
export const DRAWER_WIDTH_CLOSED = 64;

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  mobileView?: boolean;
  showMessage?: MessageType;
  setShowMessage?: React.Dispatch<React.SetStateAction<MessageType>>;
  assignmentDataFilled?: boolean;
  setOpenMessageModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setNavigateTo?: React.Dispatch<React.SetStateAction<string>>;
  isErrorComponent?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({
  sidebarOpen,
  setSidebarOpen,
  mobileView,
  setShowMessage,
  showMessage,
  assignmentDataFilled,
  setOpenMessageModal,
  setNavigateTo,
  isErrorComponent,
}) => {
  const [openLanguageModal, setOpenLanguageModal] = useState(false);
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { customisations } = useCustomisation();
  const { portalUid } = useParams();
  const { showToast } = useToast();
  const { auth } = useAuth();
  const { portals, dispatch: portalsDispatch } = usePortal();
  const t = useTranslate("appFrame");

  const isViewingAssignment = location.pathname.startsWith("/assignment/");

  const isPortalDesign = location.pathname.startsWith("/portal-configuration/");

  const pagesWithoutPortalSelection = [
    "/portal-settings",
    "/portal-design",
    "/portal-translation",
    "/portal-pages",
    "/new-report",
    "/group-cards",
    "/assignment-uploads",
    "/email-template/",
    "/add-email-template",
    "/email-send",
    "/manage-assignments",
    "/add-new-user",
    "/edit-attributes",
    "/assignments/add",
    "/edit",
    "/edit-assignment",
    "/delete-assignment",
    "/assignment/",
    "/assignments/",
  ];

  const disablePortalsDropdown = pagesWithoutPortalSelection.some((path) =>
    location.pathname.includes(path),
  );

  const sidebarBgColor =
    customisations.customisations.GPC.backgroundSettings?.gpcSidebarBgColor ??
    brand[950];

  const sidebarShade = useMemo(
    () => getShadeColour(sidebarBgColor),
    [sidebarBgColor],
  );

  const sidebarTextColor = useMemo(
    () => getShadeColour(sidebarBgColor, 60),
    [sidebarBgColor],
  );

  const navItems = useMemo(() => {
    const allNavItems = isPortalDesign
      ? portalDesignNav(portalUid)
      : defaultNav(t);

    const canUserManagement = hasUserManagementAccess(
      auth.user,
      portals.selectedPortal,
    );
    const canUserImportJob = hasUserImportJobAccess(
      auth.user,
      portals.selectedPortal,
    );
    // Toggle gates uniformly - no Main Admin/System Admin bypass. The main portal (isMain) has
    // no toggle concept and is always allowed.
    const canManageTranslation =
      !!portals.selectedPortal?.isMain ||
      !!portals.selectedPortal?.customTranslation;

    return allNavItems
      .map((item: MainMenuItem) => {
        if (item.id === "userManagement" && item.subMenus) {
          const subMenus = item.subMenus.filter((sub) =>
            sub.id === "bulkUserActions"
              ? canUserManagement || canUserImportJob
              : canUserManagement,
          );
          return { ...item, subMenus };
        }
        if (item.id === "portalConfiguration" && item.subMenus) {
          const subMenus = item.subMenus.filter((sub) =>
            sub.id === "portalTranslation" ? canManageTranslation : true,
          );
          return { ...item, subMenus };
        }
        return item;
      })
      .filter((item: MainMenuItem) => {
        if (
          item.route &&
          !isPortalDesign &&
          portals.selectedPortal?.portalPages
        ) {
          const pageConfig = portals.selectedPortal.portalPages.find(
            (p) => p.path === item.route,
          );
          if (pageConfig && !pageConfig.isVisible) {
            return false;
          }
        }

        if (item.id === "fileHub") {
          return hasFileHubAccess(auth.user, portals.selectedPortal);
        }

        if (item.id === "adminConsole") {
          return hasPortalConfigurationAccess(
            auth.user,
            portals.selectedPortal,
          );
        }

        if (item.id === "portalConfiguration") {
          return (
            hasAdminAccess(auth.user, portals.selectedPortal) ||
            hasPortalConfigAccess(auth.user, portals.selectedPortal)
          );
        }

        if (item.id === "analytics") {
          return hasAnalyticsAccess(auth.user, portals.selectedPortal);
        }

        if (item.id === "assignments") {
          return hasAssignmentManagementAccess(
            auth.user,
            portals.selectedPortal,
          );
        }

        if (item.id === "userManagement") {
          return canUserManagement || canUserImportJob;
        }

        if (item.id === "emailManagement") {
          return hasEmailManagementAccess(auth.user, portals.selectedPortal);
        }

        return !!item.route;
      });
  }, [isPortalDesign, portalUid, auth.user, portals.selectedPortal, t]);

  // Automatically expand the menu item that contains the current route
  useEffect(() => {
    if (isPortalDesign && navItems.length > 0) {
      // Find which menu item contains the current route
      // First try exact match, then fall back to startsWith for nested routes
      const activeMenuIndex = navItems.findIndex((item) => {
        if (item.subMenus) {
          return item.subMenus.some((sub) => {
            if (!sub.route) return false;
            // Exact match first
            if (location.pathname === sub.route) return true;
            // Then check if current path starts with submenu route (for nested routes)
            // But ensure it's not just a partial match (e.g., /portal-settings shouldn't match /portal-settings-edit)
            const pathSegments = location.pathname.split("/").filter(Boolean);
            const routeSegments = sub.route.split("/").filter(Boolean);
            if (pathSegments.length >= routeSegments.length) {
              return routeSegments.every(
                (segment, idx) => pathSegments[idx] === segment,
              );
            }
            return false;
          });
        }
        return false;
      });

      // Set the open menu to the active one (or null if none found)
      if (activeMenuIndex !== -1) {
        setOpenMenu(activeMenuIndex);
      } else {
        setOpenMenu(null);
      }
    } else {
      // Reset menu state for non-portal-design routes
      setOpenMenu(null);
    }
  }, [location.pathname, isPortalDesign, navItems]);

  const handleMenuToggle = (id: number) => {
    const isOpening = openMenu !== id;
    setOpenMenu(isOpening ? id : null);

    if (isOpening) {
      const firstSubRoute = navItems[id]?.subMenus?.[0]?.route;
      if (firstSubRoute) {
        navigate(firstSubRoute);
      }
    }
  };

  const handlePortalSelectChange = (e: SelectChangeEvent<string>) => {
    const selectedPortal = portals.portals.find(
      (p) => p.portalUid === e.target.value,
    );
      if (selectedPortal) {
      portalsDispatch({
        type: "SET_SELECTED_PORTAL",
        payload: selectedPortal,
      });

    }

    if (selectedPortal && portalUid) {
      const newPortalUid = selectedPortal.portalUid;
      const newPath = location.pathname.replace(portalUid, newPortalUid);
      navigate(newPath, { replace: true });
    }
  };

  const handleNavigate = async (e: React.MouseEvent, route: string) => {
    if (isModifiedClick(e)) {
      return;
    }
    if (isViewingAssignment) {
      e.preventDefault();
      try {
        await terminateWithoutRedirect();
        navigate(route);
        if (mobileView) {
          setSidebarOpen(false);
        }
        return;
      } catch (error: any) {
        console.log("Error:", error);
        // 423 (max attempts exceeded) is expected here — the user is
        // navigating away anyway, and this is already surfaced on the
        // assignment page itself. Showing it here leaks it onto whatever
        // page `navigate(route)` lands on next, since ToastProvider is
        // mounted above the router and outlives the route change.
        if (error?.response?.status !== 423) {
          showToast(error.response.data, "error");
        }
        navigate(route);
        if (mobileView) {
          setSidebarOpen(false);
        }
        return;
      }
    }
    if (
      showMessage &&
      setShowMessage &&
      setOpenMessageModal &&
      setNavigateTo &&
      assignmentDataFilled &&
      (showMessage === "none" || showMessage === "error")
    ) {
      e.preventDefault();
      setShowMessage("error");
      setOpenMessageModal(true);
      setNavigateTo(route);
      return;
    }
    // Close sidebar on mobile for regular navigation
    if (mobileView) {
      setSidebarOpen(false);
    }
  };

  const isPortalSelectDisabled = disablePortalsDropdown || !!isErrorComponent;

  const drawerContent = (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: sidebarOpen ? "space-between" : "center",
          padding: mobileView || sidebarOpen ? "0 1rem" : "0",
          minHeight: 64,
          backgroundColor: sidebarBgColor,
        }}
      >
        <SidebarHeader
          sidebarOpen={sidebarOpen}
          mobileView={mobileView}
          isPortalDesign={isPortalDesign}
          sidebarShade={sidebarShade}
          sidebarTextColor={sidebarTextColor}
          onOpen={() => setSidebarOpen(true)}
          onClose={() => setSidebarOpen(false)}
        />
      </Box>

      {mobileView && portals.selectedPortal && (
        <Box sx={{ padding: "16px 12px", paddingBottom: 0 }}>
          <Select
            id="sidebar-portal-select"
            disabled={isPortalSelectDisabled}
            IconComponent={ExpandMoreIcon}
            value={portals.selectedPortal?.portalUid ?? ""}
            onChange={handlePortalSelectChange}
            MenuProps={{
              disableScrollLock: true,
              PaperProps: {
                sx: {
                  backgroundColor: sidebarBgColor,
                },
              },
            }}
            size="small"
            fullWidth
            sx={{
              backgroundColor: sidebarBgColor,
              border: `1px solid ${sidebarShade}`,
              "&:hover": {
                backgroundColor: sidebarBgColor,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: sidebarShade,
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: sidebarShade,
              },
              "& .MuiSelect-icon": {
                color: sidebarShade,
              },
              ...(portals.selectedPortal.isMain && {
                backgroundColor: `var(--brand-600)`,
                border: `1px solid var(--brand-400)`,
                "&:hover": {
                  backgroundColor: `var(--brand-600)`,
                },
                "&.Mui-disabled": {
                  backgroundColor: lighten(brand[600], 0.7),
                },
                "&.Mui-disabled .MuiTypography-body1": {
                  WebkitTextFillColor: "white",
                },
                "& .MuiSelect-icon": {
                  color: "white",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--brand-400)",
                },
              }),
            }}
            renderValue={(selected) => {
              const selectedPortal = portals.portals?.find(
                (p) => p.portalUid === selected,
              );
              return (
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: portals.selectedPortal?.isMain
                      ? "white"
                      : sidebarTextColor,
                  }}
                >
                  {selectedPortal?.name || selected}
                </Typography>
              );
            }}
          >
            {portals.portals?.map((item) => {
              const isSelected =
                item.portalUid === portals.selectedPortal?.portalUid;
              return (
                <MuiMenuItem
                  key={item.portalUid}
                  value={item.portalUid}
                  selected={isSelected}
                  sx={{
                    backgroundColor: sidebarBgColor,
                    ...(item.isMain && {
                      "&.Mui-selected, &.Mui-focusVisible, &.Mui-selected.Mui-focusVisible":
                        {
                          backgroundColor: "#F9F5FF !important",
                          "&:hover": {
                            backgroundColor: "#F9F5FF !important",
                            textDecoration: "underline",
                          },
                        },
                      "&:hover": {
                        backgroundColor: "#F9F5FF !important",
                        textDecoration: "underline",
                      },
                    }),
                    "&.Mui-selected, &.Mui-focusVisible, &.Mui-selected.Mui-focusVisible, &.Mui-selected:hover":
                      {
                        backgroundColor: sidebarShade,
                      },
                    "&:hover, &.Mui-focusVisible, &.Mui-selected.Mui-focusVisible, &.Mui-selected:hover":
                      {
                        backgroundColor: sidebarShade,
                        textDecoration: "underline",
                      },
                  }}
                >
                  <Typography
                    sx={{
                      flex: 1,
                      fontWeight: isSelected ? 600 : 400,
                      color: sidebarTextColor,
                    }}
                  >
                    {item.name}
                  </Typography>
                  {isSelected && (
                    <CheckIcon sx={{ color: brand[600], fontSize: "20px" }} />
                  )}
                </MuiMenuItem>
              );
            })}
          </Select>
        </Box>
      )}

      <List
        component="nav"
        sx={{
          flexGrow: 1,
          padding: sidebarOpen ? "16px 12px" : "8px 0",
          overflow: "auto",
        }}
      >
        {navItems.map((item, index) => (
          <React.Fragment key={index}>
            {isPortalDesign && item.id === "home" && (
              <Divider
                sx={{ marginY: "1rem", backgroundColor: sidebarTextColor }}
              />
            )}
            <MenuItem
              item={item}
              index={index}
              openMenu={openMenu}
              onMenuToggle={handleMenuToggle}
              sidebarOpen={sidebarOpen}
              sidebarShade={sidebarShade}
              sidebarTextColor={sidebarTextColor}
              onNavigate={handleNavigate}
              mobileView={mobileView}
              setSidebarOpen={setSidebarOpen}
            />
          </React.Fragment>
        ))}

        <UserMenu
          sidebarOpen={sidebarOpen}
          sidebarShade={sidebarShade}
          sidebarTextColor={sidebarTextColor}
          mobileView={mobileView}
          setSidebarOpen={setSidebarOpen}
          onLanguageModalOpen={() => {
            if (isErrorComponent || isViewingAssignment) return;
            setOpenLanguageModal(true);
          }}
        />
      </List>

      <LanguageChooser
        open={openLanguageModal}
        onClose={() => setOpenLanguageModal(false)}
      />
    </>
  );

  const drawerPaperStyles = mobileView
    ? {
        width: "100%",
        backgroundColor: sidebarBgColor,
        borderRadius: 0,
        padding: 0,
      }
    : {
        width: sidebarOpen ? `${DRAWER_WIDTH}px` : `${DRAWER_WIDTH_CLOSED}px`,
        transition: (theme: any) =>
          theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: sidebarOpen
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
        overflowX: "hidden",
        backgroundColor: sidebarBgColor,
        borderRadius: 0,
        padding: 0,
      };

  return (
    <Drawer
      id="sidebar-drawer"
      variant={mobileView ? "temporary" : "permanent"}
      open={sidebarOpen}
      anchor={mobileView ? "right" : undefined}
      onClose={mobileView ? () => setSidebarOpen(false) : undefined}
      slotProps={{
        paper: {
          sx: drawerPaperStyles,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};
